import React, { useState, useMemo } from 'react';
import { Box } from '@mui/material';
import WorkInfoList from '../ui/WorkInfoList';
import SearchBar from '../../../Components/Ui/SearchBar';
import FilterBar from '../../../Components/Ui/FilterBar';
import TabBar from '../../../Components/Ui/TabBar';
import AddFAB from '../../../Components/Ui/AddFAB';
import { useDispatch } from 'react-redux';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import {
  setDepCd,
  setGongjongCd,
  setYear,
  setMonth,
} from '../../../Reducer/SearchReducer';

const TAB_RISK = 'risk';
const TAB_NEAR_MISS = 'nearMiss';

const TAB_CONFIG = [
  { key: TAB_RISK, label: '위험요인 제보' },
  { key: TAB_NEAR_MISS, label: '아차사고 상황' },
];

export default function M_M_RNM_BASIC(props) {
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_RISK);

  const HandleAddClick = () => {
    navigate('/M_RNM/choose');
  };

  const HandleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const HandleTabChange = (key) => {
    setActiveTab(key);
  };

  const tabFilteredList = useMemo(() => {
    const list = props.workinfoData ?? [];
    const gubunValue =
      activeTab === TAB_RISK ? '위험요인 제보' : '아차사고 상황';
    return list.filter((item) => item.gubun === gubunValue);
  }, [props.workinfoData, activeTab]);

  const filteredData = useMemo(() => {
    if (!searchKeyword.trim()) return tabFilteredList;
    const kw = searchKeyword.trim().toLowerCase();
    return tabFilteredList.filter(
      (item) =>
        (item.content && item.content.toLowerCase().includes(kw)) ||
        (item.gongjongNm && item.gongjongNm.toLowerCase().includes(kw))
    );
  }, [tabFilteredList, searchKeyword]);

  const stats = useMemo(() => {
    const list = tabFilteredList;
    const completed = list.filter((item) => item.status === '완료').length;
    return {
      total: list.length,
      completed,
      inProgress: list.length - completed,
    };
  }, [tabFilteredList]);

  const filterState = props.filterState ?? {};

  const depOptions = (props.depList ?? []).map((d) => ({
    value: d.value ?? '',
    label: d.name ?? d.label ?? '',
  }));
  const gongjongCdOptions = (props.gongjongList ?? []).map((p) => ({
    value: p.value ?? '',
    label: p.name ?? p.label ?? '',
  }));
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 10 }).map((_, idx) => {
        const y = new Date().getFullYear() - idx;
        return { value: String(y), label: `${y}년` };
      }),
    []
  );
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, idx) => {
        const m = idx + 1;
        return { value: String(m), label: `${m}월` };
      }),
    []
  );

  const filterItems = useMemo(
    () => [
      { key: 'dep', label: '부서', value: filterState.depName ?? '전체' },
      {
        key: 'gongjongCd',
        label: '공정',
        value: filterState.gongjongNm ?? '전체',
      },
      { key: 'year', label: '년도', value: filterState.year ?? '' },
      {
        key: 'month',
        label: '월',
        value: filterState.month ? `${Number(filterState.month)}월` : '',
      },
    ],
    [
      filterState.depName,
      filterState.gongjongNm,
      filterState.year,
      filterState.month,
    ]
  );

  const filterOptionsByKey = useMemo(
    () => ({
      dep: depOptions,
      gongjongCd: gongjongCdOptions,
      year: yearOptions,
      month: monthOptions,
    }),
    [depOptions, gongjongCdOptions, yearOptions, monthOptions]
  );

  const HandleFilterSelect = (key, option) => {
    const override = {};
    if (key === 'dep') {
      dispatch(setDepCd(option.value));
      override.depCd = option.value;
    } else if (key === 'gongjongCd') {
      dispatch(setGongjongCd(option.value));
      override.gongjongCd = option.value;
    } else if (key === 'year') {
      dispatch(setYear(option.value));
      override.year = option.value;
    } else if (key === 'month') {
      dispatch(setMonth(option.value));
      override.month = option.value;
    }

    if (props.SearchLogic1) {
      props.SearchLogic1(override);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8fafc]">
      <SearchBar
        value={searchKeyword}
        onChange={HandleSearchChange}
        placeholder="제보 내용 또는 공정명 검색"
      />

      <TabBar
        items={TAB_CONFIG}
        activeKey={activeTab}
        onTabChange={HandleTabChange}
      />

      <FilterBar
        items={filterItems}
        optionsByKey={filterOptionsByKey}
        onSelect={HandleFilterSelect}
      />

      {/* 통계 요약 */}
      <div className="flex gap-2 px-4 py-4">
        <div className="flex-1 rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
          <p className="mb-1 text-[11px] font-bold text-slate-500 uppercase">
            전체 건수
          </p>
          <p className="text-xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="flex-1 rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
          <p className="mb-1 text-[11px] font-bold text-slate-500 uppercase">
            조치 완료
          </p>
          <p className="text-xl font-bold text-[#22c55e]">{stats.completed}</p>
        </div>
        <div className="flex-1 rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
          <p className="mb-1 text-[11px] font-bold text-slate-500 uppercase">
            확인 중
          </p>
          <p className="text-xl font-bold text-[#f59e0b]">{stats.inProgress}</p>
        </div>
      </div>

      {/* 카드 리스트 (Virtuoso) */}
      <div className="flex min-h-0 flex-1 flex-col px-4">
        {filteredData.length === 0 ? (
          <Box
            sx={{
              px: 1,
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              flex: 1,
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#64748b',
            }}
          >
            <p>
              {tabFilteredList.length > 0
                ? '검색 결과가 없습니다.'
                : activeTab === TAB_RISK
                  ? '등록된 위험요인 제보가 없습니다.'
                  : '등록된 아차사고 상황이 없습니다.'}
            </p>
          </Box>
        ) : (
          <WorkInfoList
            workinfoData={filteredData}
            setSelectedWorkInfo={props.setSelectedWorkInfo}
            selectedWorkInfo={props.selectedWorkInfo}
            DeleteLogic1={props.DeleteLogic1}
          />
        )}
      </div>

      <AddFAB handleAddClick={HandleAddClick} />
    </div>
  );
}
